import React, { useEffect, useRef, useState } from "react";
import { Box, Modal, Typography, CircularProgress } from "@mui/material";
import JSZip from "jszip";
import mammoth from "mammoth";

/**
 * Self-contained file preview used ONLY by the Upload Report screen.
 * Handles the file picked through "Choose file" (or imported from the uploaded
 * documents modal) without touching any existing preview flow.
 *
 * Supported inline: zip (with per-entry preview), xlsx/xlsm, csv, docx (text),
 * pdf, images and plain text. Everything else falls back to a download button.
 */

const MAX_INLINE_PREVIEW_BYTES = 5 * 1024 * 1024;
const MAX_SHEET_ROWS = 500;
const MAX_SHEET_COLS = 60;
const MAX_TEXT_LINES = 2000;
const MAX_ZIP_DEPTH = 2;

const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "bmp", "webp", "svg"];
const TEXT_EXTENSIONS = ["txt", "json", "xml", "log", "md", "html", "htm", "yml", "yaml"];
const XLSX_DATE_FORMAT_IDS = new Set([14, 15, 16, 17, 18, 19, 20, 21, 22, 45, 46, 47]);

const extensionOf = (name) => {
  const parts = String(name || "").toLowerCase().split(".");
  return parts.length > 1 ? parts.pop() : "";
};

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const getPreviewKind = (name, mimeType = "") => {
  const ext = extensionOf(name);
  const mime = String(mimeType || "").toLowerCase();

  // Office formats are zips as well, so they must be matched before "zip".
  if (ext === "xlsx" || ext === "xlsm" || mime.includes("spreadsheetml")) return "sheet";
  if (ext === "docx" || mime.includes("wordprocessingml")) return "docx";
  if (ext === "csv") return "csv";
  if (ext === "xls" || ext === "doc") return "legacyOffice";
  if (
    ext === "zip" ||
    mime === "application/zip" ||
    mime === "application/x-zip-compressed"
  )
    return "zip";
  if (ext === "pdf" || mime === "application/pdf") return "pdf";
  if (IMAGE_EXTENSIONS.includes(ext) || mime.startsWith("image/")) return "image";
  if (TEXT_EXTENSIONS.includes(ext) || mime.startsWith("text/")) return "text";
  return "unsupported";
};

const MIME_BY_EXTENSION = {
  pdf: "application/pdf",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  bmp: "image/bmp",
  webp: "image/webp",
  svg: "image/svg+xml",
  txt: "text/plain",
  log: "text/plain",
  md: "text/plain",
  // html is deliberately served as plain text - never let zip content run as a page
  html: "text/plain",
  htm: "text/plain",
  json: "application/json",
  xml: "application/xml",
  csv: "text/csv",
  zip: "application/zip",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  xlsm: "application/vnd.ms-excel.sheet.macroEnabled.12",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
};

/**
 * JSZip hands out entry blobs with an empty MIME type (see jszip/lib/zipObject.js),
 * and a blob: URL with no type makes the browser render a PDF as raw text.
 * Re-wrap the blob with the type implied by its name.
 */
const withMimeType = (blob, fileName) => {
  const type = MIME_BY_EXTENSION[extensionOf(fileName)] || "application/octet-stream";
  if (blob && blob.type === type) return blob;
  return new Blob([blob], { type });
};

const readBlobArrayBuffer = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Could not read the file"));
    reader.readAsArrayBuffer(blob);
  });

const readBlobText = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Could not read the file"));
    reader.readAsText(blob);
  });

const parseXmlFromZip = async (zip, path) => {
  const entry = zip.file(path);
  if (!entry) return null;
  const xml = await entry.async("string");
  return new DOMParser().parseFromString(xml, "application/xml");
};

const textOfNodes = (nodeList) =>
  Array.from(nodeList || [])
    .map((node) => node.textContent || "")
    .join("");

const columnIndexFromRef = (ref) => {
  let index = 0;
  for (let i = 0; i < ref.length; i += 1) {
    const code = ref.charCodeAt(i);
    if (code >= 65 && code <= 90) index = index * 26 + (code - 64);
    else if (code >= 97 && code <= 122) index = index * 26 + (code - 96);
    else break;
  }
  return index - 1;
};

/** Excel stores dates as a serial number counted from 1899-12-30. */
const excelSerialToText = (serial) => {
  const value = Number(serial);
  const date = new Date(Math.round((value - 25569) * 86400 * 1000));
  if (Number.isNaN(date.getTime())) return String(serial);
  const pad = (num) => String(num).padStart(2, "0");
  const datePart = `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(
    date.getUTCDate()
  )}`;
  const hasTime = Math.abs(value % 1) > 1e-6;
  return hasTime
    ? `${datePart} ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`
    : datePart;
};

const readSharedStrings = async (zip) => {
  const doc = await parseXmlFromZip(zip, "xl/sharedStrings.xml");
  if (!doc) return [];
  return Array.from(doc.getElementsByTagName("si")).map((si) =>
    textOfNodes(si.getElementsByTagName("t"))
  );
};

/** Style indexes whose number format renders as a date. */
const readDateStyles = async (zip) => {
  const dateStyles = new Set();
  const doc = await parseXmlFromZip(zip, "xl/styles.xml");
  if (!doc) return dateStyles;

  const customDateFormats = new Set();
  Array.from(doc.getElementsByTagName("numFmt")).forEach((fmt) => {
    const id = Number(fmt.getAttribute("numFmtId"));
    const code = String(fmt.getAttribute("formatCode") || "")
      .toLowerCase()
      .replace(/\[[^\]]*\]/g, "")
      .replace(/"[^"]*"/g, "");
    if (/[ymd]/.test(code)) customDateFormats.add(id);
  });

  const cellXfs = doc.getElementsByTagName("cellXfs")[0];
  if (cellXfs) {
    Array.from(cellXfs.getElementsByTagName("xf")).forEach((xf, index) => {
      const id = Number(xf.getAttribute("numFmtId") || 0);
      if (XLSX_DATE_FORMAT_IDS.has(id) || customDateFormats.has(id)) {
        dateStyles.add(index);
      }
    });
  }
  return dateStyles;
};

const readCellValue = (cellEl, sharedStrings, dateStyles) => {
  const type = cellEl.getAttribute("t");

  if (type === "inlineStr") {
    const inline = cellEl.getElementsByTagName("is")[0];
    return inline ? textOfNodes(inline.getElementsByTagName("t")) : "";
  }

  const valueEl = cellEl.getElementsByTagName("v")[0];
  const raw = valueEl ? valueEl.textContent || "" : "";
  if (raw === "") return "";

  if (type === "s") {
    const shared = sharedStrings[Number(raw)];
    return shared === undefined ? "" : shared;
  }
  if (type === "b") return raw === "1" ? "TRUE" : "FALSE";
  if (type === "e" || type === "str") return raw;

  const styleIndex = Number(cellEl.getAttribute("s") || -1);
  if (dateStyles.has(styleIndex) && !Number.isNaN(Number(raw))) {
    return excelSerialToText(raw);
  }
  return raw;
};

const readSheetRows = async (zip, path, sharedStrings, dateStyles) => {
  const doc = await parseXmlFromZip(zip, path);
  if (!doc) return { rows: [], truncated: false };

  const rowEls = Array.from(doc.getElementsByTagName("row"));
  const rows = [];
  let truncated = false;

  for (let i = 0; i < rowEls.length; i += 1) {
    if (rows.length >= MAX_SHEET_ROWS) {
      truncated = true;
      break;
    }
    const row = [];
    Array.from(rowEls[i].getElementsByTagName("c")).forEach((cellEl) => {
      const ref = cellEl.getAttribute("r") || "";
      const colIndex = ref ? columnIndexFromRef(ref) : row.length;
      if (colIndex >= 0 && colIndex < MAX_SHEET_COLS) {
        row[colIndex] = readCellValue(cellEl, sharedStrings, dateStyles);
      }
    });
    for (let c = 0; c < row.length; c += 1) {
      if (row[c] === undefined) row[c] = "";
    }
    rows.push(row);
  }

  const width = rows.reduce((max, row) => Math.max(max, row.length), 0);
  rows.forEach((row) => {
    while (row.length < width) row.push("");
  });

  return { rows, truncated };
};

/** Minimal OOXML reader - enough for the Master/Detail templates. */
const readXlsxSheets = async (blob) => {
  const zip = await JSZip.loadAsync(blob);
  const sharedStrings = await readSharedStrings(zip);
  const dateStyles = await readDateStyles(zip);

  const relsDoc = await parseXmlFromZip(zip, "xl/_rels/workbook.xml.rels");
  const relMap = {};
  if (relsDoc) {
    Array.from(relsDoc.getElementsByTagName("Relationship")).forEach((rel) => {
      relMap[rel.getAttribute("Id")] = rel.getAttribute("Target");
    });
  }

  const sheetDefs = [];
  const workbookDoc = await parseXmlFromZip(zip, "xl/workbook.xml");
  if (workbookDoc) {
    const sheetsEl = workbookDoc.getElementsByTagName("sheets")[0];
    Array.from(sheetsEl ? sheetsEl.getElementsByTagName("sheet") : []).forEach(
      (sheetEl, index) => {
        const relId =
          sheetEl.getAttributeNS(
            "http://schemas.openxmlformats.org/officeDocument/2006/relationships",
            "id"
          ) || sheetEl.getAttribute("r:id");
        const target = relMap[relId];
        let path = target
          ? target.startsWith("/")
            ? target.slice(1)
            : `xl/${target}`
          : `xl/worksheets/sheet${index + 1}.xml`;
        path = path.replace("xl/xl/", "xl/");
        sheetDefs.push({
          name: sheetEl.getAttribute("name") || `Sheet${index + 1}`,
          path,
        });
      }
    );
  }

  if (sheetDefs.length === 0) {
    zip.forEach((path) => {
      if (/^xl\/worksheets\/sheet\d+\.xml$/.test(path)) {
        sheetDefs.push({ name: path.split("/").pop(), path });
      }
    });
  }

  if (sheetDefs.length === 0) {
    throw new Error("No worksheet found in this file");
  }

  const sheets = [];
  for (let i = 0; i < sheetDefs.length; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    const { rows, truncated } = await readSheetRows(
      zip,
      sheetDefs[i].path,
      sharedStrings,
      dateStyles
    );
    sheets.push({ name: sheetDefs[i].name, rows, truncated });
  }
  return sheets;
};

/** Strip anything executable before the converted document is injected. */
const sanitizeDocxHtml = (html) => {
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, "text/html");
  Array.from(
    doc.querySelectorAll("script, iframe, object, embed, link, style, meta, base")
  ).forEach((node) => node.remove());

  Array.from(doc.querySelectorAll("*")).forEach((el) => {
    Array.from(el.attributes).forEach((attr) => {
      const name = attr.name.toLowerCase();
      const value = String(attr.value || "").trim().toLowerCase();
      if (name.startsWith("on")) el.removeAttribute(attr.name);
      if (
        (name === "href" || name === "src") &&
        (value.startsWith("javascript:") || value.startsWith("vbscript:"))
      ) {
        el.removeAttribute(attr.name);
      }
    });
  });

  return doc.body.innerHTML;
};

/** mammoth keeps tables, headings, lists and inline images (as data URIs). */
const readDocxHtml = async (blob) => {
  const arrayBuffer = await readBlobArrayBuffer(blob);
  const result = await mammoth.convertToHtml({ arrayBuffer });
  const html = sanitizeDocxHtml(result && result.value ? result.value : "");
  return html || "<p>This document has no readable content.</p>";
};

const parseCsv = (text) => {
  const rows = [];
  let row = [];
  let value = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    if (inQuotes) {
      if (char === '"') {
        if (text[i + 1] === '"') {
          value += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        value += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ",") {
      row.push(value);
      value = "";
    } else if (char === "\n" || char === "\r") {
      if (char === "\r" && text[i + 1] === "\n") i += 1;
      row.push(value);
      rows.push(row);
      row = [];
      value = "";
      if (rows.length >= MAX_SHEET_ROWS) return { rows, truncated: true };
    } else {
      value += char;
    }
  }
  if (value !== "" || row.length > 0) {
    row.push(value);
    rows.push(row);
  }
  return { rows, truncated: false };
};

const downloadBlob = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = name || "file";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const CenteredNote = ({ children, color = "#94a3b8" }) => (
  <Box
    sx={{
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      p: 2,
    }}
  >
    <Typography
      sx={{ color, fontStyle: "italic", fontSize: "0.85rem", textAlign: "center" }}
    >
      {children}
    </Typography>
  </Box>
);

const Loader = ({ label }) => (
  <Box
    sx={{
      height: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 1.5,
    }}
  >
    <CircularProgress size={28} sx={{ color: "#0E3F66" }} />
    <Typography sx={{ fontSize: "0.8rem", color: "#6b7280" }}>{label}</Typography>
  </Box>
);

function ObjectUrlPreview({ blob, name, asImage }) {
  const [url, setUrl] = useState("");

  useEffect(() => {
    const objectUrl = URL.createObjectURL(blob);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [blob]);

  if (!url) return <Loader label="Opening preview..." />;

  return asImage ? (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#f8fafc",
      }}
    >
      <img
        src={url}
        alt={name}
        style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
      />
    </Box>
  ) : (
    <iframe
      src={url}
      title={name}
      style={{
        width: "100%",
        height: "100%",
        border: "1px solid #E5E7EB",
        borderRadius: "6px",
      }}
    />
  );
}

function DataTable({ rows, truncated }) {
  if (!rows || rows.length === 0) {
    return <CenteredNote>This sheet is empty.</CenteredNote>;
  }
  return (
    <Box sx={{ height: "100%", overflow: "auto" }}>
      <table
        className="table table-sm table-bordered"
        style={{ fontSize: "0.72rem", whiteSpace: "nowrap", marginBottom: 4 }}
      >
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) =>
                rowIndex === 0 ? (
                  <th
                    key={cellIndex}
                    style={{ background: "#f1f5f9", fontWeight: 600 }}
                  >
                    {cell}
                  </th>
                ) : (
                  <td key={cellIndex}>{cell}</td>
                )
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {truncated && (
        <Typography sx={{ fontSize: "0.7rem", color: "#b45309", pb: 1 }}>
          Showing the first {MAX_SHEET_ROWS} rows only.
        </Typography>
      )}
    </Box>
  );
}

function SheetPreview({ blob, name }) {
  const [state, setState] = useState({ loading: true, error: "", sheets: [] });
  const [activeSheet, setActiveSheet] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, error: "", sheets: [] });
    setActiveSheet(0);

    readXlsxSheets(blob)
      .then((sheets) => {
        if (!cancelled) setState({ loading: false, error: "", sheets });
      })
      .catch((err) => {
        console.log("Sheet preview error:", err);
        if (!cancelled) {
          setState({
            loading: false,
            error: "Could not read this spreadsheet.",
            sheets: [],
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [blob]);

  if (state.loading) return <Loader label="Reading spreadsheet..." />;
  if (state.error) {
    return (
      <UnsupportedPreview
        blob={blob}
        name={name}
        message={state.error}
      />
    );
  }

  const sheet = state.sheets[activeSheet] || state.sheets[0];

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {state.sheets.length > 1 && (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
          {state.sheets.map((item, index) => (
            <Typography
              key={item.name}
              component="span"
              onClick={() => setActiveSheet(index)}
              sx={{
                fontSize: "0.72rem",
                fontWeight: 600,
                cursor: "pointer",
                px: 1,
                py: 0.4,
                borderRadius: "4px",
                color: index === activeSheet ? "#fff" : "#0E3F66",
                bgcolor: index === activeSheet ? "#0E3F66" : "#e2e8f0",
              }}
            >
              {item.name}
            </Typography>
          ))}
        </Box>
      )}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <DataTable rows={sheet?.rows} truncated={sheet?.truncated} />
      </Box>
    </Box>
  );
}

function CsvPreview({ blob }) {
  const [state, setState] = useState({ loading: true, error: "", data: null });

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, error: "", data: null });
    readBlobText(blob)
      .then((text) => {
        if (!cancelled) setState({ loading: false, error: "", data: parseCsv(text) });
      })
      .catch(() => {
        if (!cancelled)
          setState({ loading: false, error: "Could not read this file.", data: null });
      });
    return () => {
      cancelled = true;
    };
  }, [blob]);

  if (state.loading) return <Loader label="Reading file..." />;
  if (state.error) return <CenteredNote color="#b45309">{state.error}</CenteredNote>;
  return <DataTable rows={state.data.rows} truncated={state.data.truncated} />;
}

function TextPreview({ blob }) {
  const [state, setState] = useState({ loading: true, error: "", text: "" });

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, error: "", text: "" });
    readBlobText(blob)
      .then((text) => {
        const lines = text.split(/\r?\n/);
        const clipped = lines.slice(0, MAX_TEXT_LINES).join("\n");
        if (!cancelled)
          setState({
            loading: false,
            error: "",
            text:
              lines.length > MAX_TEXT_LINES
                ? `${clipped}\n\n... showing the first ${MAX_TEXT_LINES} lines only.`
                : clipped,
          });
      })
      .catch(() => {
        if (!cancelled)
          setState({ loading: false, error: "Could not read this file.", text: "" });
      });
    return () => {
      cancelled = true;
    };
  }, [blob]);

  if (state.loading) return <Loader label="Reading file..." />;
  if (state.error) return <CenteredNote color="#b45309">{state.error}</CenteredNote>;

  return (
    <Box sx={{ height: "100%", overflow: "auto", bgcolor: "#f8fafc", p: 1.5 }}>
      <pre
        style={{
          margin: 0,
          fontSize: "0.72rem",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {state.text}
      </pre>
    </Box>
  );
}

function DocxPreview({ blob, name }) {
  const [state, setState] = useState({ loading: true, error: "", html: "" });

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, error: "", html: "" });
    readDocxHtml(blob)
      .then((html) => {
        if (!cancelled) setState({ loading: false, error: "", html });
      })
      .catch((err) => {
        console.log("Docx preview error:", err);
        if (!cancelled)
          setState({
            loading: false,
            error: "Could not read this document.",
            html: "",
          });
      });
    return () => {
      cancelled = true;
    };
  }, [blob]);

  if (state.loading) return <Loader label="Reading document..." />;
  if (state.error)
    return <UnsupportedPreview blob={blob} name={name} message={state.error} />;

  return (
    <Box
      sx={{
        height: "100%",
        overflow: "auto",
        bgcolor: "#fff",
        p: 2,
        "& p": { fontSize: "0.82rem", margin: "0 0 8px" },
        "& h1, & h2, & h3, & h4": {
          color: "#0E3F66",
          fontSize: "0.95rem",
          margin: "12px 0 8px",
        },
        "& table": {
          borderCollapse: "collapse",
          width: "100%",
          marginBottom: "12px",
        },
        "& td, & th": {
          border: "1px solid #d1d5db",
          padding: "4px 8px",
          fontSize: "0.78rem",
          verticalAlign: "top",
        },
        "& th": { background: "#f1f5f9", fontWeight: 600 },
        "& ul, & ol": { paddingLeft: "20px", fontSize: "0.82rem" },
        "& li": { marginBottom: "4px" },
        "& img": { maxWidth: "100%", height: "auto" },
      }}
      dangerouslySetInnerHTML={{ __html: state.html }}
    />
  );
}

function UnsupportedPreview({ blob, name, message }) {
  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 1.5,
        p: 2,
      }}
    >
      <Typography
        sx={{
          fontWeight: 700,
          color: "#0E3F66",
          border: "2px solid #0E3F66",
          borderRadius: "6px",
          px: 2,
          py: 0.75,
        }}
      >
        {(extensionOf(name) || "file").toUpperCase()}
      </Typography>
      <Typography sx={{ fontSize: "0.82rem", color: "#6b7280", textAlign: "center" }}>
        {message || "Preview is not available for this file type."}
      </Typography>
      <button
        type="button"
        className="btn btn__secondary btn__rounded"
        onClick={() => downloadBlob(blob, name)}
      >
        <span>Download</span>
      </button>
    </Box>
  );
}

function ZipPreview({ blob, depth }) {
  const [state, setState] = useState({ loading: true, error: "", entries: [] });
  const [selected, setSelected] = useState(null);
  const [entry, setEntry] = useState({ loading: false, blob: null, error: "" });
  const zipRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, error: "", entries: [] });
    setSelected(null);
    setEntry({ loading: false, blob: null, error: "" });

    JSZip.loadAsync(blob)
      .then((zip) => {
        if (cancelled) return;
        zipRef.current = zip;
        const entries = [];
        zip.forEach((path, file) => {
          if (file.dir) return;
          if (path.startsWith("__MACOSX/")) return;
          entries.push({
            path,
            name: path.split("/").pop(),
            size: file._data && file._data.uncompressedSize,
          });
        });
        entries.sort((a, b) => a.path.localeCompare(b.path));
        setState({ loading: false, error: "", entries });
        if (entries.length > 0) setSelected(entries[0]);
      })
      .catch((err) => {
        console.log("Zip preview error:", err);
        if (!cancelled) {
          setState({
            loading: false,
            error:
              "Could not open this zip. It may be corrupted or password protected.",
            entries: [],
          });
        }
      });

    return () => {
      cancelled = true;
      zipRef.current = null;
    };
  }, [blob]);

  useEffect(() => {
    if (!selected || !zipRef.current) return undefined;
    let cancelled = false;
    setEntry({ loading: true, blob: null, error: "" });

    zipRef.current
      .file(selected.path)
      .async("blob")
      .then((fileBlob) => {
        if (!cancelled)
          setEntry({
            loading: false,
            blob: withMimeType(fileBlob, selected.name),
            error: "",
          });
      })
      .catch(() => {
        if (!cancelled)
          setEntry({
            loading: false,
            blob: null,
            error: "Could not extract this file from the zip.",
          });
      });

    return () => {
      cancelled = true;
    };
  }, [selected]);

  if (state.loading) return <Loader label="Opening zip..." />;
  if (state.error) return <CenteredNote color="#b45309">{state.error}</CenteredNote>;
  if (state.entries.length === 0)
    return <CenteredNote>This zip has no files inside.</CenteredNote>;

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: 1.5,
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          width: { xs: "100%", md: 260 },
          flexShrink: 0,
          maxHeight: { xs: 150, md: "100%" },
          overflowY: "auto",
          border: "1px solid #e5e7eb",
          borderRadius: "6px",
          p: 0.5,
        }}
      >
        <Typography
          sx={{ fontSize: "0.7rem", color: "#6b7280", px: 0.75, py: 0.5 }}
        >
          {state.entries.length} file(s) inside
        </Typography>
        {state.entries.map((item) => {
          const isActive = selected && selected.path === item.path;
          return (
            <Box
              key={item.path}
              onClick={() => setSelected(item)}
              sx={{
                cursor: "pointer",
                px: 0.75,
                py: 0.6,
                borderRadius: "4px",
                bgcolor: isActive ? "#e0f2f1" : "transparent",
                borderLeft: isActive ? "3px solid #21cdc0" : "3px solid transparent",
                "&:hover": { bgcolor: isActive ? "#e0f2f1" : "#f1f5f9" },
              }}
            >
              <Typography
                title={item.path}
                sx={{
                  fontSize: "0.72rem",
                  fontWeight: isActive ? 700 : 500,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {item.name}
              </Typography>
              <Typography sx={{ fontSize: "0.65rem", color: "#6b7280" }}>
                {item.path.includes("/") ? `${item.path.split("/").slice(0, -1).join("/")}/ · ` : ""}
                {formatBytes(item.size)}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
        {entry.loading ? (
          <Loader label="Extracting file..." />
        ) : entry.error ? (
          <CenteredNote color="#b45309">{entry.error}</CenteredNote>
        ) : entry.blob && selected ? (
          <PreviewBody blob={entry.blob} name={selected.name} depth={depth + 1} />
        ) : (
          <CenteredNote>Select a file to preview.</CenteredNote>
        )}
      </Box>
    </Box>
  );
}

function PreviewBody({ blob, name, depth = 0 }) {
  const kind = getPreviewKind(name, blob && blob.type);
  const size = blob ? blob.size : 0;

  if (!blob) return <CenteredNote>No file selected.</CenteredNote>;

  if (kind === "zip") {
    if (depth > MAX_ZIP_DEPTH) {
      return (
        <UnsupportedPreview
          blob={blob}
          name={name}
          message="Nested zip is too deep to open here."
        />
      );
    }
    return <ZipPreview blob={blob} depth={depth} />;
  }

  if (kind === "image") return <ObjectUrlPreview blob={blob} name={name} asImage />;
  if (kind === "pdf") return <ObjectUrlPreview blob={blob} name={name} />;

  if (kind === "sheet" || kind === "csv" || kind === "text" || kind === "docx") {
    if (size > MAX_INLINE_PREVIEW_BYTES) {
      return (
        <UnsupportedPreview
          blob={blob}
          name={name}
          message={`This file is ${formatBytes(size)} - too large to preview here.`}
        />
      );
    }
    if (kind === "sheet") return <SheetPreview blob={blob} name={name} />;
    if (kind === "csv") return <CsvPreview blob={blob} />;
    if (kind === "docx") return <DocxPreview blob={blob} name={name} />;
    return <TextPreview blob={blob} />;
  }

  if (kind === "legacyOffice") {
    return (
      <UnsupportedPreview
        blob={blob}
        name={name}
        message="Old Office formats (.xls / .doc) cannot be previewed in the browser. Please download the file, or use .xlsx / .docx."
      />
    );
  }

  return <UnsupportedPreview blob={blob} name={name} />;
}

const UploadReportFilePreview = ({ open, file, fileName, onClose }) => {
  const name = fileName || (file && file.name) || "Selected file";

  return (
    <Modal open={!!open} onClose={onClose} aria-labelledby="upload-report-file-preview">
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "95%", md: "85%" },
          height: { xs: "80vh", md: "85vh" },
          bgcolor: "background.paper",
          borderRadius: "10px",
          boxShadow: 24,
          p: 2,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            mb: 1,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              id="upload-report-file-preview"
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: "#0E3F66",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {name}
            </Typography>
            {file && (
              <Typography sx={{ fontSize: "0.7rem", color: "#6b7280" }}>
                {formatBytes(file.size)}
              </Typography>
            )}
          </Box>
          <Typography
            component="span"
            onClick={onClose}
            sx={{
              color: "#0E3F66",
              textDecoration: "underline",
              cursor: "pointer",
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            Close
          </Typography>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0 }}>
          {open && file ? <PreviewBody blob={file} name={name} /> : null}
        </Box>
      </Box>
    </Modal>
  );
};

export default UploadReportFilePreview;
